import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Download,
  Loader2,
  Monitor,
  Smartphone,
  Tablet,
  TrendingUp,
  Users2,
  MousePointerClick,
  Eye,
  HelpCircle,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * AdminReports, Relatórios de Acesso e Interação (Área 3).
 * Filtros: hoje / 7d / 30d / 90d / personalizado.
 * Métricas: KPIs · diário · hourly (se today) · devices · top pages/events/CTAs · funis.
 * Export CSV.
 */

const QUICK_RANGES = [
  { v: "today", label: "Hoje" },
  { v: "7d", label: "7 dias" },
  { v: "30d", label: "30 dias" },
  { v: "90d", label: "90 dias" },
];

export default function AdminReports() {
  const [range, setRange] = useState("7d");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams({ range });
    if (range === "custom" && custom.from && custom.to) {
      p.set("from", custom.from);
      p.set("to", custom.to);
    }
    return p;
  }, [range, custom]);

  const load = useCallback(() => {
    if (range === "custom" && (!custom.from || !custom.to)) return;
    setLoading(true);
    setErr(null);
    api
      .get(`/admin/reports/overview?${params}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Falha ao carregar."))
      .finally(() => setLoading(false));
  }, [params, range, custom]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const exportCSV = () => {
    const token = localStorage.getItem("token") || "";
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    fetch(`${apiBase}/api/admin/reports/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `reports_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const totals = data?.totals || {};
  const conv = data?.conversions || {};

  return (
    <section
      data-testid="admin-reports-section"
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#C5A059] mb-1">
            <BarChart3 size={12} strokeWidth={1.6} />
            <span className="text-[0.6rem] uppercase tracking-[0.28em] font-medium">
              Relatórios
            </span>
          </div>
          <h2 className="font-serif-display text-[1.4rem] text-[#0B1E36] tracking-[-0.01em]">
            Acessos e interações
          </h2>
        </div>
        <button
          data-testid="admin-reports-export"
          onClick={exportCSV}
          className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36] bg-[#C5A059] hover:bg-[#D4B273] font-medium px-4 py-2.5"
        >
          <Download size={13} strokeWidth={1.7} />
          Exportar CSV
        </button>
      </div>

      {/* Range filters */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="flex border border-[#0B1E36]/15">
          {QUICK_RANGES.map((r) => (
            <button
              key={r.v}
              data-testid={`admin-reports-range-${r.v}`}
              onClick={() => setRange(r.v)}
              className={`text-[0.62rem] uppercase tracking-[0.2em] px-3.5 py-2 transition-colors ${
                range === r.v
                  ? "bg-[#0B1E36] text-white font-medium"
                  : "bg-white text-[#0B1E36]/65 hover:bg-[#FAF8F4]"
              }`}
            >
              {r.label}
            </button>
          ))}
          <button
            data-testid="admin-reports-range-custom"
            onClick={() => setRange("custom")}
            className={`text-[0.62rem] uppercase tracking-[0.2em] px-3.5 py-2 transition-colors ${
              range === "custom"
                ? "bg-[#0B1E36] text-white font-medium"
                : "bg-white text-[#0B1E36]/65 hover:bg-[#FAF8F4]"
            }`}
          >
            Personalizado
          </button>
        </div>
        {range === "custom" && (
          <div className="flex items-center gap-2">
            <input
              data-testid="admin-reports-from"
              type="date"
              value={custom.from}
              onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
              className="bg-white border border-[#0B1E36]/15 px-2.5 py-1.5 text-xs text-[#0B1E36]"
            />
            <span className="text-[#0B1E36]/45">→</span>
            <input
              data-testid="admin-reports-to"
              type="date"
              value={custom.to}
              onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
              className="bg-white border border-[#0B1E36]/15 px-2.5 py-1.5 text-xs text-[#0B1E36]"
            />
          </div>
        )}
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 mb-4 text-sm">
          {err}
        </div>
      )}

      {loading || !data ? (
        <div className="text-center py-12 text-[#0B1E36]/45">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Kpi icon={Eye} label="Page views" value={totals.pageviews || 0} />
            <Kpi icon={Users2} label="Sessões únicas" value={totals.unique_sessions || 0} />
            <Kpi icon={MousePointerClick} label="Eventos" value={totals.events || 0} />
            <Kpi icon={TrendingUp} label="Leads no período" value={conv.leads_in_range || 0} />
          </div>

          {/* Daily bars */}
          {data.daily && data.daily.length > 0 && (
            <div className="border border-[#0B1E36]/10 bg-[#FAF8F4] p-4 mb-6">
              <h3 className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 mb-3">
                Eventos por dia
              </h3>
              <DailyBars items={data.daily} />
            </div>
          )}

          {/* Hourly when today */}
          {range === "today" && data.hourly && data.hourly.length > 0 && (
            <div className="border border-[#0B1E36]/10 bg-[#FAF8F4] p-4 mb-6">
              <h3 className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 mb-3">
                Eventos por hora
              </h3>
              <HourlyBars items={data.hourly} />
            </div>
          )}

          {/* Devices + Conversions */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="border border-[#0B1E36]/10 p-4">
              <h3 className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 mb-3">
                Dispositivos
              </h3>
              <DevicesSplit devices={data.devices} />
            </div>
            <div className="border border-[#0B1E36]/10 p-4">
              <h3 className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 mb-3">
                Conversões no período
              </h3>
              <ul className="space-y-1.5 text-sm">
                <ConvRow label="Leads (mensagens diárias)" value={conv.leads_in_range || 0} />
                <ConvRow label="Assinantes de planos" value={conv.plan_subscribers_in_range || 0} />
                <ConvRow label="Inscritos devocional diário" value={conv.daily_subscribers_in_range || 0} />
                <ConvRow label="Leads totais (histórico)" value={conv.leads_total || 0} />
              </ul>
            </div>
          </div>

          {/* Top lists */}
          <div className="grid md:grid-cols-3 gap-4">
            <TopList title="Top páginas" items={data.top_pages} testid="admin-reports-top-pages" />
            <TopList title="Top eventos" items={data.top_events} testid="admin-reports-top-events" />
            <TopList title="Top CTAs" items={data.top_ctas} testid="admin-reports-top-ctas" />
          </div>

          <p className="mt-5 text-[0.62rem] uppercase tracking-[0.18em] text-[#0B1E36]/45">
            LGPD · sem cookies · anônimo · IP não armazenado
          </p>
        </>
      )}
    </section>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="border border-[#0B1E36]/10 bg-[#FAF8F4] p-4">
      <div className="flex items-center gap-2 text-[#C5A059] mb-2">
        <Icon size={12} strokeWidth={1.6} />
        <span className="text-[0.55rem] uppercase tracking-[0.22em] font-medium">{label}</span>
      </div>
      <p className="text-[1.7rem] text-[#0B1E36] tabular-nums font-light leading-none">{value}</p>
    </div>
  );
}

function DailyBars({ items }) {
  const max = Math.max(...items.map((d) => d.events), 1);
  return (
    <div className="flex items-end gap-1 h-32" data-testid="admin-reports-daily-bars">
      {items.map((d) => (
        <div key={d.day} className="flex-1 flex flex-col items-center group">
          <div
            className="w-full bg-[#C5A059] hover:bg-[#0B1E36] transition-colors"
            style={{ height: `${(d.events / max) * 100}%`, minHeight: "2px" }}
            title={`${d.day}: ${d.events} eventos · ${d.sessions} sessões`}
          />
          <span className="text-[0.55rem] text-[#0B1E36]/45 mt-1 tabular-nums hidden md:block">
            {d.day.slice(8)}
          </span>
        </div>
      ))}
    </div>
  );
}

function HourlyBars({ items }) {
  const max = Math.max(...items.map((d) => d.events), 1);
  const hours = Array.from({ length: 24 }, (_, h) => {
    const found = items.find((d) => d.hour === h);
    return { hour: h, events: found?.events || 0 };
  });
  return (
    <div className="flex items-end gap-1 h-24" data-testid="admin-reports-hourly-bars">
      {hours.map((d) => (
        <div key={d.hour} className="flex-1 flex flex-col items-center">
          <div
            className="w-full bg-[#0B1E36]"
            style={{ height: `${(d.events / max) * 100}%`, minHeight: "1px" }}
            title={`${d.hour}h: ${d.events}`}
          />
          <span className="text-[0.55rem] text-[#0B1E36]/45 mt-1 tabular-nums">{d.hour}</span>
        </div>
      ))}
    </div>
  );
}

function DevicesSplit({ devices }) {
  const total = (devices?.mobile || 0) + (devices?.tablet || 0) + (devices?.desktop || 0) + (devices?.unknown || 0);
  if (total === 0) return <p className="text-[0.8rem] text-[#0B1E36]/45 italic">Sem dados.</p>;
  const rows = [
    { key: "desktop", icon: Monitor, label: "Desktop" },
    { key: "tablet", icon: Tablet, label: "Tablet" },
    { key: "mobile", icon: Smartphone, label: "Mobile" },
    { key: "unknown", icon: HelpCircle, label: "Outro" },
  ];
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const v = devices[r.key] || 0;
        const pct = Math.round((v / total) * 100);
        const Icon = r.icon;
        return (
          <div key={r.key} data-testid={`admin-reports-device-${r.key}`}>
            <div className="flex items-center justify-between text-[0.78rem] text-[#0B1E36]/75 mb-0.5">
              <span className="inline-flex items-center gap-1.5">
                <Icon size={12} strokeWidth={1.6} />
                {r.label}
              </span>
              <span className="tabular-nums">{v} · {pct}%</span>
            </div>
            <div className="h-1.5 bg-[#FAF8F4]">
              <div className="h-full bg-[#C5A059]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ConvRow({ label, value }) {
  return (
    <li className="flex items-center justify-between border-b border-[#0B1E36]/8 pb-1.5 last:border-0">
      <span className="text-[#0B1E36]/65">{label}</span>
      <span className="tabular-nums font-medium text-[#0B1E36]">{value}</span>
    </li>
  );
}

function TopList({ title, items, testid }) {
  return (
    <div data-testid={testid} className="border border-[#0B1E36]/10 p-4">
      <h3 className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 mb-3">{title}</h3>
      {!items || items.length === 0 ? (
        <p className="text-[0.78rem] text-[#0B1E36]/45 italic">Sem dados.</p>
      ) : (
        <ul className="space-y-1.5">
          {items.slice(0, 10).map((it, i) => (
            <li key={i} className="flex items-center justify-between text-[0.8rem]">
              <span className="text-[#0B1E36]/70 truncate mr-2">{it.value || "·"}</span>
              <span className="text-[#0B1E36] tabular-nums font-medium">{it.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
